import React from 'react';
import { Collapse, Tag } from 'antd';
import { NewsResult, NewsRanking, NewsOrder } from './types';
import { orderEvidence } from './newsOrdering';
import RankingHint from './RankingHint';
import { readable, shortDate } from './format';

type Source = {
  news_id: number;
  headline: string;
  published_at: string;
  timing: string;
  source_kind: string;
  identity_verified_by?: string[] | string;
};
type Group = {
  event_id: number;
  derived_title: string;
  event_type?: string;
  derived_event_type?: string;
  event_time?: string;
  status: string;
  fact_level: string;
  sources: Source[];
  first_verified_publication?: string;
  ranking?: NewsRanking;
};

const CorpusGroups: React.FC<{ news: NewsResult; order?: NewsOrder }> = ({
  news,
  order = 'oldest',
}) => {
  const groups = orderEvidence(
    (news.events || []) as unknown as Group[],
    order,
    group => ({
      id: group.event_id,
      timestamp: group.first_verified_publication,
      ranking: group.ranking,
    }),
  );
  if (!groups.length) return null;
  return (
    <Collapse
      ghost
      size="small"
      className="market-explorer__corpus-groups"
      items={[
        {
          key: 'groups',
          label: `Verified event groups · ${groups.length}`,
          children: (
            <>
              <p>
                Group labels summarize linked reports. Article headlines below
                retain the original source text.
              </p>
              <Collapse
                size="small"
                items={groups.map(group => ({
                  key: String(group.event_id),
                  label: (
                    <span>
                      <span>{group.derived_title}</span>{' '}
                      <RankingHint ranking={group.ranking} source="corpus" />
                      <small>
                        {group.first_verified_publication
                          ? ` · First published ${shortDate(group.first_verified_publication)}`
                          : ''}{' '}
                        · {group.sources?.length || 0} sources
                      </small>
                    </span>
                  ),
                  children: (
                    <div>
                      <Tag>
                        {readable(
                          group.event_type ||
                            group.derived_event_type ||
                            'event',
                        )}
                      </Tag>
                      {group.fact_level && (
                        <Tag>{readable(group.fact_level)}</Tag>
                      )}
                      {(group.sources || []).map((source, index) => (
                        <div
                          className="market-explorer__group-source"
                          key={`${source.news_id}:${index}`}
                        >
                          <strong>{source.headline}</strong>
                          <small>
                            {source.published_at
                              ? shortDate(source.published_at)
                              : 'Publication time unavailable'}{' '}
                            · {readable(source.timing || 'timing unavailable')}{' '}
                            · {readable(source.source_kind || 'source article')}
                          </small>
                          <small>
                            Source identity checked:{' '}
                            {(Array.isArray(source.identity_verified_by)
                              ? source.identity_verified_by
                                  .map(readable)
                                  .join(', ')
                              : typeof source.identity_verified_by === 'string'
                                ? readable(source.identity_verified_by)
                                : '') || 'PM article reference'}
                          </small>
                        </div>
                      ))}
                    </div>
                  ),
                }))}
              />
            </>
          ),
        },
      ]}
    />
  );
};

export default CorpusGroups;
